import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LoadingScreen } from '@/components/ui/Feedback/LoadingScreen';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { routes } from '@/config/routes';
import { useAuth } from '@/hooks/useAuth';

// Lazy load page components
const Home = lazy(() => import('@/pages/Home'));
const About = lazy(() => import('@/pages/About'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Guide = lazy(() => import('@/pages/Guide'));

// Lazy load BLAST related pages
const Upload = lazy(() => import('@/pages/blast/Upload'));
const Analysis = lazy(() => import('@/pages/blast/Analysis'));
const Results = lazy(() => import('@/pages/blast/Results'));

// Lazy load Admin related pages
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const GenomeManager = lazy(() => import('@/pages/admin/GenomeManager'));
const Settings = lazy(() => import('@/pages/admin/Settings'));

// Lazy load Auth pages
const SignIn = lazy(() => import('@/pages/auth/SignIn'));
const Register = lazy(() => import('@/pages/auth/Register'));

// Lazy load Legal pages
const Terms = lazy(() => import('@/pages/legal/Terms'));

const App = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public Routes */}
          <Route path={routes.home} element={<Home />} />
          <Route path={routes.about} element={<About />} />
          <Route path={routes.guide} element={<Guide />} />
          <Route path={routes.login.signIn} element={<SignIn />} />
          <Route path={routes.login.register} element={<Register />} />
          
          {/* BLAST Routes */}
          <Route path={routes.blast.upload} element={<Upload />} />
          <Route path={routes.blast.analysis} element={<Analysis />} />
          <Route path={routes.blast.results} element={<Results />} />
          
          {/* Protected Admin Routes */}
          <Route 
            path={routes.admin.dashboard} 
            element={
              <ProtectedRoute role="admin" user={user}>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path={routes.admin.genomeManager} 
            element={
              <ProtectedRoute role="admin" user={user}>
                <GenomeManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path={routes.admin.settings} 
            element={
              <ProtectedRoute role="admin" user={user}>
                <Settings />
              </ProtectedRoute>
            } 
          />
          
          {/* Legal Routes */}
          <Route path={routes.legal.terms} element={<Terms />} />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;